import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { LineChartConfig } from 'src/app/core/charts/configs';
import { ShadowItTrendsResponse } from 'src/app/core/models/analytics/es-models';

// The API sends no colors: categories take the palette in rank order, Other stays grey.
const CATEGORY_COLORS = ['#49419a', '#fd7e14', '#28a745', '#e83e8c', '#dc3545', '#17a2b8'];
const OTHER_COLOR = '#6c757d';
const SERIES_COLORS: Record<string, string> = { Pending: '#dc3545', Dismissed: '#28a745' };

@Component({
  selector: 'app-es-shadow-it-trend',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule],
  templateUrl: './es-shadow-it-trend.component.html',
  styleUrl: './es-shadow-it-trend.component.scss',
})
export class EsShadowItTrendComponent extends BaseComponent {
  shadowItTrendsData = input<ShadowItTrendsResponse>();

  get trendIcon(): string {
    const trendValue = this.shadowItTrendsData()?.trendPercentageChange || 0;
    if (trendValue > 0) {
      return '↑';
    } else if (trendValue < 0) {
      return '↓';
    }
    return '→';
  }

  get isTrendUp(): boolean {
    const trendValue = this.shadowItTrendsData()?.trendPercentageChange || 0;
    return trendValue > 0;
  }

  get isTrendDown(): boolean {
    const trendValue = this.shadowItTrendsData()?.trendPercentageChange || 0;
    return trendValue < 0;
  }

  get formattedTrendChange(): string {
    const trendValue = this.shadowItTrendsData()?.trendPercentageChange || 0;
    return `${Math.abs(trendValue)}% Pending Average vs Last Period`;
  }

  get categories(): Array<{ category: string; percentage: number; color: string }> {
    return (this.shadowItTrendsData()?.categoryBreakdown ?? []).map((item, index) => ({
      ...item,
      color: item.category === 'Other' ? OTHER_COLOR : CATEGORY_COLORS[index % CATEGORY_COLORS.length],
    }));
  }

  get chartOptions(): EChartsOption {
    const data = this.shadowItTrendsData();
    if (!data || !data.chartData || data.chartData.series.length === 0) {
      return this.getEmptyChartOptions();
    }

    const chartData = {
      categories: data.chartData.categories,
      series: data.chartData.series.map((series) => ({
        ...series,
        color: SERIES_COLORS[series.name] ?? OTHER_COLOR,
        smooth: true,
        showDataLabels: false,
      })),
      showLegend: true,
      rotateLabels: false,
    };

    return LineChartConfig.create(chartData, {
      backgroundColor: 'transparent',
      grid: {
        left: '3%',
        right: '4%',
        top: '5%',
        containLabel: true,
      },
    });
  }

  private getEmptyChartOptions(): EChartsOption {
    return LineChartConfig.create(
      {
        categories: [],
        series: [],
      },
      {
        tooltip: {
          formatter: () => 'No data available',
        },
      }
    );
  }

  constructor() {
    super();
  }
}
